/**
 * Unified tracking facade — fans events out to ALL registered destinations.
 *
 * Currently wired:
 *   - PostHog (product analytics — `lib/analytics.js`)
 *   - GA4     (marketing/SEO   — `lib/ga4.js`)
 *
 * Stubs ready for plug-in (env-var driven, zero rewrites needed):
 *   - Meta Pixel       (REACT_APP_META_PIXEL_ID)
 *   - Pinterest Tag    (REACT_APP_PINTEREST_TAG_ID)
 *   - TikTok Pixel     (REACT_APP_TIKTOK_PIXEL_ID)
 *
 * Consent gate: every adapter is consent-aware. Until the user accepts
 * analytics cookies, no network call is made and nothing is queued.
 *
 * One canonical event shape across the platform:
 *   trackEvent('add_to_cart', { item_id, item_name, price_gbp, currency: 'GBP' })
 *
 * GA4 receives the snake_case event name + cleaned params directly.
 * PostHog continues to receive the same event under the same name, so
 * existing PostHog dashboards / funnels keep working.
 */

import { initGA4, gaEvent, gaPageView, gaSetUserProperty } from "./ga4";
import { initAnalytics as initPosthog, _capturePosthogOnly } from "./analytics";

// ─── Consent ──────────────────────────────────────────────────────────────

const CONSENT_KEY = "fg_cookie_consent_v1";

export function hasConsent() {
  try {
    return localStorage.getItem(CONSENT_KEY) === "all";
  } catch {
    return false;
  }
}

// ─── Pixel adapter registry (future-proof for Meta / Pinterest / TikTok) ──

/**
 * Each adapter implements: { id, isEnabled(), init(), trackEvent(name, params), trackPageView(path, search, title) }.
 * Adapters self-report via env vars so the facade never has to know about them.
 */
const adapters = [];

function registerAdapter(adapter) {
  if (!adapter || typeof adapter !== "object") return;
  adapters.push(adapter);
}

// PostHog adapter — already implemented in lib/analytics.js
registerAdapter({
  id: "posthog",
  isEnabled: () => !!process.env.REACT_APP_POSTHOG_KEY,
  init: initPosthog,
  trackEvent: (name, params) => _capturePosthogOnly(name, params || {}),
  trackPageView: (path, search) => _capturePosthogOnly("$pageview", { path, search }),
});

// GA4 adapter
registerAdapter({
  id: "ga4",
  isEnabled: () => !!process.env.REACT_APP_GA4_MEASUREMENT_ID,
  init: initGA4,
  trackEvent: gaEvent,
  trackPageView: gaPageView,
});

// Meta Pixel — stub, activates automatically when REACT_APP_META_PIXEL_ID is set.
// Implementation will follow Meta's standard fbq snippet pattern; placeholder no-op for now.
registerAdapter({
  id: "meta_pixel",
  isEnabled: () => !!process.env.REACT_APP_META_PIXEL_ID,
  init: () => {
    // TODO when Meta key is provided: lazy-inject Facebook Pixel base code + fbq('init', ID).
    // Keep the same pattern as GA4 — consent-gated, send_page_view false, manual page_view dispatch.
    return false;
  },
  trackEvent: (name, params) => {
    if (typeof window === "undefined" || !window.fbq) return;
    try {
      window.fbq("trackCustom", name, params || {});
    } catch { /* swallow */ }
  },
  trackPageView: () => {
    if (typeof window === "undefined" || !window.fbq) return;
    try { window.fbq("track", "PageView"); } catch { /* swallow */ }
  },
});

// Pinterest Tag — stub
registerAdapter({
  id: "pinterest_tag",
  isEnabled: () => !!process.env.REACT_APP_PINTEREST_TAG_ID,
  init: () => false, // TODO: pintrk('load', ID); pintrk('page'); consent-gated load.
  trackEvent: (name, params) => {
    if (typeof window === "undefined" || !window.pintrk) return;
    try { window.pintrk("track", name, params || {}); } catch { /* swallow */ }
  },
  trackPageView: () => {
    if (typeof window === "undefined" || !window.pintrk) return;
    try { window.pintrk("page"); } catch { /* swallow */ }
  },
});

// TikTok Pixel — stub
registerAdapter({
  id: "tiktok_pixel",
  isEnabled: () => !!process.env.REACT_APP_TIKTOK_PIXEL_ID,
  init: () => false, // TODO: ttq.load(ID); ttq.page(); consent-gated load.
  trackEvent: (name, params) => {
    if (typeof window === "undefined" || !window.ttq) return;
    try { window.ttq.track(name, params || {}); } catch { /* swallow */ }
  },
  trackPageView: () => {
    if (typeof window === "undefined" || !window.ttq) return;
    try { window.ttq.page(); } catch { /* swallow */ }
  },
});

// ─── Public API ───────────────────────────────────────────────────────────

let bootstrapped = false;

/**
 * Initialise every enabled + consented adapter. Idempotent.
 * Called once on app load (if consent already given) AND when the user
 * accepts cookies via the consent banner.
 */
export function initTracking() {
  if (bootstrapped) return;
  if (!hasConsent()) return;
  adapters.forEach((a) => {
    try {
      if (a.isEnabled()) a.init();
    } catch { /* never break the app */ }
  });
  bootstrapped = true;
}

/**
 * Fire a unified custom event across every consented destination.
 * Use snake_case names so GA4 reports look canonical.
 *
 * Returns a no-op when consent has not been granted.
 */
export function trackEvent(name, params = {}) {
  if (!hasConsent() || !name) return;
  // Lazy bootstrap in case consent was granted mid-session and we missed it.
  if (!bootstrapped) initTracking();
  adapters.forEach((a) => {
    try {
      if (a.isEnabled()) a.trackEvent(name, params);
    } catch { /* swallow */ }
  });
}

/**
 * Fire a unified page_view across every consented destination.
 * Title is read from `document.title` if not provided.
 */
export function trackPageView(path, search = "", title = "") {
  if (!hasConsent()) return;
  if (!bootstrapped) initTracking();
  const t = title || (typeof document !== "undefined" ? document.title : "");
  adapters.forEach((a) => {
    try {
      if (a.isEnabled()) a.trackPageView(path, search, t);
    } catch { /* swallow */ }
  });
}

/**
 * Set a user property on every analytics destination that supports it.
 * GA4 = user_properties, PostHog = identify props (already wired separately).
 */
export function trackUserProperty(key, value) {
  if (!hasConsent() || !key) return;
  try { gaSetUserProperty(key, value); } catch { /* swallow */ }
}

/** Returns the list of currently active adapter ids for debugging. */
export function activeAdapters() {
  return adapters.filter((a) => a.isEnabled()).map((a) => a.id);
}
